import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Balance as LegalIcon,
  Receipt as DocumentIcon,
  Assessment as ReportIcon,
  ContactPage as ClientsIcon,
  AccountBalance as CaseIcon,
  Article as ContractsIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
} from "@mui/icons-material";
import PeopleIcon from "@mui/icons-material/People";
import ContactMailIcon from "@mui/icons-material/ContactMail";

const LegalSidebar = ({ forceCollapsed = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeMenu, setActiveMenu] = useState(null);
  
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    } else if (isSmallScreen || isMobile) {
      setCollapsed(true);
    } else if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [forceCollapsed, isSmallScreen, isMobile, isTablet]);

  const handleDrawerToggle = () => {
    if (!isMobile && !forceCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const drawerWidth = collapsed ? 72 : 280;

  const menuItems = [
    {
      id: 1,
      title: "Dashboard",
      icon: <DashboardIcon />,
      path: "/Legal",
    },
    {
      id: 28,
      title: "Sub Users",
      icon: <PeopleIcon />,
      items: [
        {
          id: 21,
          name: "User Creation",
          icon: <ContactMailIcon />,
          path: "/Legal/SubUserCreation",
        },
        {
          id: 22,
          name: "SUb User Management",
          icon: <ContactMailIcon />,
          path: "/Legal/SubUserManagement",
        },
      ],
    },
    {
      id: 2,
      title: "Legal Matters",
      icon: <LegalIcon />,
      path: "/LegalMatters",
      items: [
        { name: "New Case", path: "/NewCase", icon: <CaseIcon /> },
        { name: "Active Cases", path: "/LegalActiveCases", icon: <CaseIcon /> },
        { name: "Research", path: "/LegalResearch", icon: <DocumentIcon /> },
      ],
    },
    {
      id: 3,
      title: "Documents",
      icon: <DocumentIcon />,
      path: "/Documents",
      items: [
        { name: "Document", path: "/userDocument", icon: <ContractsIcon /> },
        { name: "Upload Documents", path: "/Upload", icon: <DocumentIcon /> },
      ],
    },
    {
      id: 4,
      title: "Clients",
      icon: <ClientsIcon />,
      path: "/Clients",
      items: [
        {
          name: "Client Selection",
          path: "/Legal/ClientSelection",
          icon: <ClientsIcon />,
        },
        {
          name: "Client Companies",
          path: "/Legal/ClientCompanies",
          icon: <ClientsIcon />,
        },
     
        {
          name: "Client Tasks",
          path: "/Legal/ClientTasks",
          icon: <ReportIcon />,
        },
      ],
    },
    {
      id: 5,
      title: "Case Management",
      icon: <CaseIcon />,
      path: "/CaseManagement",
      items: [
        {
          name: "Case Tracking",
          path: "/LegalCaseTracking",
          icon: <CaseIcon />,
        },
        {
          name: "Case Timeline",
          path: "/LegalCaseTimeline",
          icon: <ReportIcon />,
        },
      ],
    },
    {
      id: 6,
      title: "Reporting",
      icon: <ReportIcon />,
      path: "/Reporting",
      items: [
        {
          name: "Performance Reports",
          path: "/LegalPerformance",
          icon: <ReportIcon />,
        },
        { name: "Analytics", path: "/LegalAnaytics", icon: <ReportIcon /> },
      ],
    },
  ];

  useEffect(() => {
    if (location.pathname.includes("/Legal/Client")) {
      setActiveMenu(4);
    }
  }, [location.pathname]);

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      <ListItemButton
        onClick={() => (item.items ? handleMenuClick(item.id) : null)}
        selected={activeMenu === item.id || location.pathname === item.path}
        component={item.items ? "div" : Link}
        to={item.items ? undefined : item.path}
        sx={{
          minHeight: 48,
          px: collapsed ? 1.5 : 2.5,
          margin: "4px 16px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "#FFF6E9",
            transform: "translateX(4px)",
            "& .MuiListItemText-primary": {
              color: "#D32F2F",
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: "#D32F2F",
            },
          },
          "&.Mui-selected": {
            backgroundColor: "#FFF6E9",
            "& .MuiListItemText-primary": {
              color: "#D32F2F",
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: "#D32F2F",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 3,
            justifyContent: "center",
            color:
              location.pathname === item.path
                ? "#D32F2F"
                : theme.palette.primary.main,
          }}
        >
          <Tooltip
            title={item.title}
            placement="right"
            disableHoverListener={!collapsed}
          >
            {item.icon}
          </Tooltip>
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color:
                    location.pathname === item.path ? "#D32F2F" : "inherit",
                },
              }}
            />
            {item.items &&
              (activeMenu === item.id ? (
                <KeyboardArrowRight sx={{ color: "#D32F2F" }} />
              ) : (
                <KeyboardArrowDown sx={{ color: "#D32F2F" }} />
              ))}
          </>
        )}
      </ListItemButton>

      {!collapsed && item.items && (
        <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem, index) => (
              <ListItemButton
                key={index}
                component={Link}
                to={subItem.path}
                selected={location.pathname === subItem.path}
                sx={{
                  pl: 4,
                  margin: "2px 16px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#FFF6E9",
                    transform: "translateX(4px)",
                    "& .MuiListItemText-primary": {
                      color: "#D32F2F",
                      fontWeight: "bold",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#D32F2F",
                    },
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#FFF6E9",
                    "& .MuiListItemText-primary": {
                      color: "#D32F2F",
                      fontWeight: "bold",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#D32F2F",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 2,
                    justifyContent: "center",
                    color:
                      location.pathname === subItem.path
                        ? "#D32F2F"
                        : theme.palette.primary.main,
                  }}
                >
                  {subItem.icon}
                </ListItemIcon>
                <ListItemText
                  primary={subItem.name}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.8rem",
                      fontWeight:
                        location.pathname === subItem.path ? 600 : 400,
                      color:
                        location.pathname === subItem.path
                          ? "#D32F2F"
                          : "inherit",
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: "black",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              Legal
            </Typography>
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: "#D32F2F",
                fontSize: "1.5rem",
                fontWeight: "bold",
                ml: 1,
              }}
            >
              Portal
            </Typography>
          </Box>
        )}
        {!isMobile && !forceCollapsed && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>{menuItems.map(renderMenuItem)}</List>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: "#D32F2F",
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default LegalSidebar;
